import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Heart, Mail, Lock, User, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, googleLogin } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });

  const from = location.state?.from?.pathname || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let data;
      if (isLogin) {
        data = await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        data = await register(formData.email, formData.password, formData.name, "couple_admin");
        toast.success("Account created successfully!");
      }
      const role = data.user?.role;
      const redirectTo = from || (role === "super_admin" ? "/admin" : "/couple");
      navigate(redirectTo, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    googleLogin();
  };

  return (
    <div className="min-h-screen bg-wedding-black flex">
      {/* Left Side - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1606216794074-735e91aa2c92?auto=format&fit=crop&w=1200&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-wedding-black via-wedding-black/50 to-transparent"></div>
        <div className="relative z-10 flex flex-col justify-center p-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/" className="inline-block mb-12">
              <h2 className="font-serif text-3xl text-wedding-gold">MemoraOrbs</h2>
              <p className="text-wedding-ivory/50 text-sm italic">Memories You Can Hold</p>
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl text-wedding-ivory mb-6">
              Your Wedding,<br />
              <span className="text-wedding-gold">Your Story</span>
            </h1>
            <p className="text-wedding-ivory/60 text-lg max-w-md">
              Create a stunning digital experience for your guests and preserve your memories forever.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-12">
            <Link to="/">
              <h2 className="font-serif text-2xl text-wedding-gold">MemoraOrbs</h2>
              <p className="text-wedding-ivory/50 text-xs italic">Memories You Can Hold</p>
            </Link>
          </div>

          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl text-wedding-ivory mb-2">
              {isLogin ? "Welcome Back" : "Create Account"}
            </h2>
            <p className="text-wedding-ivory/60">
              {isLogin ? "Sign in to manage your wedding" : "Start your wedding journey"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-wedding-ivory/80 text-xs uppercase tracking-wider">
                  Full Name
                </Label>
                <div className="relative mt-2">
                  <User className="absolute left-0 top-1/2 transform -translate-y-1/2 w-5 h-5 text-wedding-ivory/30" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-premium pl-8"
                    data-testid="name-input"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-wedding-ivory/80 text-xs uppercase tracking-wider">
                Email
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-0 top-1/2 transform -translate-y-1/2 w-5 h-5 text-wedding-ivory/30" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-premium pl-8"
                  data-testid="email-input"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-wedding-ivory/80 text-xs uppercase tracking-wider">
                Password
              </Label>
              <div className="relative mt-2">
                <Lock className="absolute left-0 top-1/2 transform -translate-y-1/2 w-5 h-5 text-wedding-ivory/30" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-premium pl-8"
                  data-testid="password-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-wedding-black border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  {isLogin ? "Sign In" : "Create Account"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-wedding-black px-4 text-wedding-ivory/40 text-sm">or</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full btn-secondary flex items-center justify-center gap-3"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <p className="text-center mt-8 text-wedding-ivory/50">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="ml-2 text-wedding-gold hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </p>

          <p className="text-center mt-4">
            <Link to="/admin" className="text-wedding-ivory/30 hover:text-wedding-gold text-sm transition-colors">
              Admin Login →
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
